import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getShiftCodes,
  addNewShiftCode,
  getShiftCodeById,
  updateShiftCodeById,
  uploadShiftCode
} from '../../../../services/shiftCodes';
import {
  handleAddShiftCodeFulfilled,
  handleFetchShiftCodesFulfilled,
  handleFetchShiftCodeByIdFulfilled,
  handleUpdateShiftCodeFulfilled,
  handlePending,
  handleRejected,
  handleImportShiftCodeFulfilled
} from './shiftCodeExtraReducers';

export const fetchShiftCodes = createAsyncThunk('shiftCodes/fetchShiftCodes', async (params) => {
  const response = await getShiftCodes(params);
  return response.data.data;
});

export const addShiftCode = createAsyncThunk('shiftCodes/addShiftCode', async (shiftCodeData, { rejectWithValue }) => {
  try {
    const response = await addNewShiftCode(shiftCodeData);
    return response.data;
  } catch (error) {
    return rejectWithValue({
      data: error.response || 'An error occurred',
    });
  }
});

export const fetchShiftCodeById = createAsyncThunk('shiftCodes/fetchShiftCodeById', async (id) => {
  const response = await getShiftCodeById(id);
  return response.data;
});

export const updateShiftCode = createAsyncThunk('shiftCodes/updateShiftCode', async (shiftCodeData) => {
  const response = await updateShiftCodeById(shiftCodeData);
  return response.data;
});

export const importShiftCode = createAsyncThunk('shiftCodes/importShiftCode', async (shiftCodeFormData, { rejectWithValue }) => {
  try {
    const response = await uploadShiftCode(shiftCodeFormData);
    const usersCount = response.headers['x-users-count'];
    const daysCount = response.headers['x-days-count'];

    return {
      data: response.data,
      headers: {
        users_count: usersCount,
        days_count: daysCount
      }
    };
  } catch (error) {
    return rejectWithValue({
      data: error.response || 'An error occurred',
    });
  }
});

const shiftCodesSlice = createSlice({
  name: 'shiftCodes',
  initialState: {
    shiftCodes: [],
    shiftCodesOrder: [],
    shiftCodeFeedbackData: null,
    shiftCodeFeedbackHeaders: {},
    shiftCodeStatus: 'idle',
    error: null,
    currentShiftCode: null,
    currentPage: 1,
    perPage: 10,
    totalPages: 0,
    totalShiftCodes: 0,
    lastUpdated: '',
    message: null,
  },
  reducers: {
    clearMessage(state) {
      state.message = null;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchShiftCodes.pending, handlePending)
      .addCase(fetchShiftCodes.fulfilled, handleFetchShiftCodesFulfilled)
      .addCase(fetchShiftCodes.rejected, handleRejected)
      .addCase(addShiftCode.pending, handlePending)
      .addCase(addShiftCode.fulfilled, handleAddShiftCodeFulfilled)
      .addCase(addShiftCode.rejected, handleRejected)
      .addCase(fetchShiftCodeById.pending, handlePending)
      .addCase(fetchShiftCodeById.fulfilled, handleFetchShiftCodeByIdFulfilled)
      .addCase(fetchShiftCodeById.rejected, handleRejected)
      .addCase(updateShiftCode.pending, handlePending)
      .addCase(updateShiftCode.fulfilled, handleUpdateShiftCodeFulfilled)
      .addCase(updateShiftCode.rejected, handleRejected)
      .addCase(importShiftCode.pending, handlePending)
      .addCase(importShiftCode.fulfilled, handleImportShiftCodeFulfilled)
      .addCase(importShiftCode.rejected, handleRejected);
  },
});

export const { clearMessage } = shiftCodesSlice.actions;

export default shiftCodesSlice.reducer;
